import api, { getApiErrors } from '@app/services/api';
import type {
	ApiError,
	ChannelPartnerAddParams,
	ChannelPartnerAddResponse,
	ChannelPartnerDeleteParams,
	ChannelPartnerDeleteResponse,
	ChannelPartnerEditParams,
	ChannelPartnerEditResponse,
	ChannelPartnerListParams,
} from '@app/services/api.types';
import { apiEndpoints, defaultErrorMessage } from '@app/utils/constants';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import useApp from '../useApp';
import { fn } from "@app/utils";

export const createChannelPartner = async (params: ChannelPartnerAddParams): Promise<ChannelPartnerAddResponse> => {
	const url = apiEndpoints.channel.add;
	const response = await api.post(url, params);
	return response?.data?.data;
};

export const updateChannelPartner = async (params: ChannelPartnerEditParams): Promise<ChannelPartnerEditResponse> => {
	const url = apiEndpoints.channel.update(params.id);
	const response = await api.put(url, params);
	return response?.data?.data;
};

export const removeChannelPartner = async (
	params: ChannelPartnerDeleteParams
): Promise<ChannelPartnerDeleteResponse> => {
	const url = apiEndpoints.channel.delete(params.id);
	const response = await api.delete(url);
	return response?.data?.data;
};

export const exportChannelPartnerList = async (params: ChannelPartnerListParams) => {
	const url = apiEndpoints.channel.getListExport(params);
	const response = await api.get<Blob>(url, {
		responseType: 'blob',
	});
	return response;
};

const useChannelPartnerActions = () => {
	const { message } = useApp();

	const addChannelPartner = useMutation<ChannelPartnerAddResponse, AxiosError<ApiError>, ChannelPartnerAddParams>({
		mutationFn: createChannelPartner,
		onSuccess: () => {
			message.success({
				type: 'success',
				content: 'Channel Partner added successfully',
			});
		},
		onError: (error: AxiosError<ApiError>) => {
			const response = error.response?.data;
			const errorMessage = getApiErrors(response?.data, true);
			message.error({
				type: 'error',
				content: errorMessage || defaultErrorMessage,
			});
		},
		meta: {
			invalidates: [['channelPartner', 'list']],
		},
	});

	const editChannelPartner = useMutation<ChannelPartnerEditResponse, AxiosError<ApiError>, ChannelPartnerEditParams>({
		mutationFn: updateChannelPartner,
		onSuccess: () => {
			message.success({
				type: 'success',
				content: 'Channel Partner updated successfully',
			});
		},
		onError: (error: AxiosError<ApiError>) => {
			const response = error.response?.data;
			const errorMessage = getApiErrors(response?.data, true);
			message.error({
				type: 'error',
				content: errorMessage || defaultErrorMessage,
			});
		},
		meta: {
			invalidates: [['channelPartner', 'list']],
		},
	});

	const deleteChannelPartner = useMutation<
		ChannelPartnerDeleteResponse,
		AxiosError<ApiError>,
		ChannelPartnerDeleteParams
	>({
		mutationFn: removeChannelPartner,
		onSuccess: () => {
			message.success({
				type: 'success',
				content: 'Channel Partner deleted successfully',
			});
		},
		onError: (error: AxiosError<ApiError>) => {
			const response = error.response?.data;
			const errorMessage = getApiErrors(response?.data, true);
			message.error({
				type: 'error',
				content: errorMessage || defaultErrorMessage,
			});
		},
		meta: {
			invalidates: [['channelPartner', 'list']],
		},
	});

	const exportChannelPartners = useMutation<
			AxiosResponse<Blob>,
			AxiosError<ApiError>,
			ChannelPartnerListParams
		>({
			mutationFn: exportChannelPartnerList,
			onSuccess: (res) => {
				const match = res.headers['content-disposition'].match(/filename\s*=\s*"(.+)"/i);
				const filename = match?.[1] || 'channelPartner.csv';
				fn.downloadFileFromBlob({
					blob: res.data,
					fileName: filename,
				});
			},
			onError: (error: AxiosError<ApiError>) => {
				const response = error.response?.data;
				const errorMessage = getApiErrors(response?.data, true);
				message.error({
					type: 'error',
					content: errorMessage || defaultErrorMessage,
				});
			},
		});

	return {
		addChannelPartner,
		editChannelPartner,
		deleteChannelPartner,
		exportChannelPartners,
	};
};

export default useChannelPartnerActions;